import { useMemo } from 'react';

export type LocalStorageInstance<T, J extends boolean> = {
  //  This ensures that if you set shouldJsonify to true, they're gonna be dealing with JSON strings.
  get: () => J extends true ? (T | null) : (string | null);
  set: (data: T) => void;
};

//  Overrides which clarify that if you don't pass shouldJsonify, you're gonna be dealing with strings.
export function createLocalStorageInstance<T>(key: string, shouldJsonify?: true): LocalStorageInstance<T, true>;
export function createLocalStorageInstance(key: string, shouldJsonify?: false): LocalStorageInstance<string, false>;
export function createLocalStorageInstance<T>(key: string, shouldJsonify?: boolean): LocalStorageInstance<T, boolean>;
/**
 * Create a get/set pair for a given localStorage key.
 * Also stringifies/parses JSON if that's your thing.
 *
 * @param {string} key - Local storage key
 * @param {boolean} shouldJsonify - Whether to JSON stringify the value
 * @returns {LocalStorageInstance} - get/set pair for the given key
 * @example
 *
 * type MyDataType = Record<string, string>;
 * const ls = createLocalStorageInstance<MyDataType>('some_unique_ls_key');
 * // ...
 * const data = ls.get();
 * // ...
 * ls.set({  })
 */
export function createLocalStorageInstance<T>(
  key: string,
  shouldJsonify: boolean = true,
): LocalStorageInstance<T, boolean> {
  return {
    get: () => {
      const data: string | null = localStorage.getItem(key);
      if (data && shouldJsonify) {
        return JSON.parse(data) as T;
      }
      return data;
    },
    set: (rawData: T | string): void => {
      const data: string = shouldJsonify
        ? JSON.stringify(rawData)
        : rawData as string;
      localStorage.setItem(key, data);
    },
  };
}

//  Overrides which clarify that if you don't pass shouldJsonify, you're gonna be dealing with strings.
export function useLocalStorage<T>(key: string, shouldJsonify?: true): LocalStorageInstance<T, true>;
export function useLocalStorage(key: string, shouldJsonify?: false): LocalStorageInstance<string, false>;
export function useLocalStorage<T>(key: string, shouldJsonify?: boolean): LocalStorageInstance<T, boolean>;
/**
 * A hook which makes it easier to use localStorage in a React component.
 * This is also helpful for testing, as it's called within the actual component,
 * which means that you can simply mock out `createLocalStorageInstance` in the test
 * (instead of futzing with global.localStorage)
 *
 * @param {string} key - Local storage key
 * @param {boolean} shouldJsonify - Whether to JSON stringify the value
 * @returns {LocalStorageInstance} - get/set pair for the given key
 */
export function useLocalStorage<T>(
  key: string,
  shouldJsonify: boolean = true,
) {
  return useMemo(() => createLocalStorageInstance<T>(key, shouldJsonify), [key, shouldJsonify]);
}
